/**
 * Chat Inbox: a conversation list beside the selected transcript.
 *
 * Live updates arrive over the inbox WebSocket as invalidation hints; the page
 * refetches rather than patching state from the socket payload, so a dropped or
 * duplicated event cannot leave the view disagreeing with the database.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api/client';
import type {
  ChatAttachment,
  ConversationDetail,
  ConversationSummary,
} from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../components/ui';
import { formatBytes, formatDateTime } from '../lib/format';
import { useBots } from '../hooks/useBots';
import { useInboxSocket } from '../hooks/useInboxSocket';

type StatusFilter = 'open' | 'closed' | 'all';

const ROLE_STYLES: Record<string, string> = {
  visitor: 'bg-slate-100 text-slate-800',
  ai: 'bg-brand-light text-slate-800',
  agent: 'bg-brand-blue text-white',
};

const ROLE_LABELS: Record<string, string> = {
  visitor: 'Visitor',
  ai: 'Assistant',
  agent: 'Agent',
};

export function Inbox(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;

  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [counts, setCounts] = useState({ open: 0, closed: 0 });
  const [filter, setFilter] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const loadList = useCallback(async () => {
    if (!slug) return;
    try {
      const result = await api.listConversations(slug, {
        status: filter === 'all' ? undefined : filter,
        search: search || undefined,
      });
      setItems(result.items);
      setCounts({ open: result.open_count, closed: result.closed_count });
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load the inbox');
    } finally {
      setLoading(false);
    }
  }, [slug, filter, search]);

  const loadDetail = useCallback(
    async (id: string) => {
      if (!slug) return;
      setDetail(await api.getConversation(slug, id));
    },
    [slug],
  );

  useEffect(() => {
    setLoading(true);
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (activeId) void loadDetail(activeId);
  }, [activeId, loadDetail]);

  // Refetch on any activity for this bot. The list always refreshes; the open
  // transcript refreshes only when the event concerns it.
  useInboxSocket(slug, (event) => {
    void loadList();
    if (event.conversation_id === activeId) void loadDetail(event.conversation_id);
  });

  const transcriptRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight });
  }, [detail]);

  async function handleSend(): Promise<void> {
    if (!slug || !activeId || !reply.trim()) return;
    setSending(true);
    try {
      await api.sendAgentMessage(slug, activeId, reply);
      setReply('');
      await loadDetail(activeId);
      await loadList();
    } finally {
      setSending(false);
    }
  }

  async function toggleStatus(): Promise<void> {
    if (!slug || !detail) return;
    const updated =
      detail.status === 'open'
        ? await api.closeConversation(slug, detail.id)
        : await api.reopenConversation(slug, detail.id);
    setDetail(updated);
    await loadList();
  }

  async function saveNote(note: string): Promise<void> {
    if (!slug || !detail) return;
    setDetail(await api.setInternalNote(slug, detail.id, note || null));
  }

  function exportTranscript(): void {
    if (!detail) return;
    const lines = detail.messages.map(
      (message) =>
        `[${formatDateTime(message.created_at)}] ${
          ROLE_LABELS[message.role] ?? message.role
        }${message.agent_name ? ` (${message.agent_name})` : ''}: ${message.content}`,
    );
    const blob = new Blob([lines.join('\n\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = `transcript-${detail.id}.txt`;
      link.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  if (!slug) return <EmptyState title="No bot selected" />;

  return (
    <div>
      <PageHeader
        title="Chat Inbox"
        description={`${counts.open} open · ${counts.closed} closed`}
      />

      {error && <ErrorNotice message={error} />}

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <Card className="flex max-h-[calc(100vh-13rem)] flex-col">
          <div className="space-y-2 border-b border-slate-200 p-3">
            <div className="flex gap-1">
              {(['open', 'closed', 'all'] as StatusFilter[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setFilter(option)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize ${
                    filter === option
                      ? 'bg-brand-navy text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search transcripts…"
              className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-brand-blue"
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <Spinner />
            ) : items.length === 0 ? (
              <EmptyState title="No conversations" />
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(item.id)}
                      className={`w-full px-3 py-2.5 text-left hover:bg-slate-50 ${
                        activeId === item.id ? 'bg-brand-light' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-slate-800">
                          {item.contact_name ??
                            item.contact_email ??
                            `Visitor ${item.visitor_id.slice(0, 8)}`}
                        </span>
                        <StatusBadge status={item.status} />
                      </div>
                      <p className="mt-0.5 truncate text-xs text-slate-500">
                        {item.last_message_preview ?? 'No messages yet'}
                      </p>
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                        <span>{formatDateTime(item.last_message_at)}</span>
                        <span>· {item.message_count} msgs</span>
                        {item.human_involved && <span>· human</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {detail ? (
          <div className="grid gap-4 xl:grid-cols-[1fr_18rem]">
            <Card className="flex max-h-[calc(100vh-13rem)] flex-col">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
                <div className="text-sm text-slate-600">
                  Started {formatDateTime(detail.started_at)}
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={exportTranscript}>
                    Export
                  </Button>
                  <Button variant="secondary" onClick={() => void toggleStatus()}>
                    {detail.status === 'open' ? 'Close' : 'Reopen'}
                  </Button>
                </div>
              </div>

              <div ref={transcriptRef} className="flex-1 space-y-3 overflow-y-auto p-4">
                {detail.messages.map((message) => (
                  <div
                    key={message.id}
                    className={message.role === 'visitor' ? 'flex' : 'flex justify-end'}
                  >
                    <div className="max-w-[80%]">
                      <div className="mb-0.5 text-[11px] text-slate-400">
                        {ROLE_LABELS[message.role] ?? message.role}
                        {message.agent_name ? ` · ${message.agent_name}` : ''} ·{' '}
                        {formatDateTime(message.created_at)}
                      </div>
                      <div
                        className={`whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                          ROLE_STYLES[message.role] ?? 'bg-slate-100'
                        }`}
                      >
                        {message.content}
                      </div>
                      {message.attachments.map((file) => (
                        <AttachmentCard
                          key={file.id}
                          slug={slug}
                          conversationId={detail.id}
                          file={file}
                          onError={setError}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-200 p-3">
                <div className="flex gap-2">
                  <textarea
                    rows={2}
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        void handleSend();
                      }
                    }}
                    placeholder="Join the conversation as a human agent…"
                    className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-blue"
                  />
                  <Button onClick={() => void handleSend()} disabled={sending}>
                    {sending ? 'Sending…' : 'Send'}
                  </Button>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">
                  Sending marks this conversation as human-handled and delivers it to
                  the visitor immediately.
                </p>
              </div>
            </Card>

            <MetadataSidebar detail={detail} onSaveNote={saveNote} />
          </div>
        ) : (
          <Card>
            <EmptyState
              title="Select a conversation"
              hint="Pick one from the list to read the transcript and reply."
            />
          </Card>
        )}
      </div>
    </div>
  );
}

function MetadataSidebar({
  detail,
  onSaveNote,
}: {
  detail: ConversationDetail;
  onSaveNote: (note: string) => Promise<void>;
}): JSX.Element {
  const [note, setNote] = useState(detail.internal_note ?? '');
  useEffect(
    () => setNote(detail.internal_note ?? ''),
    [detail.id, detail.internal_note],
  );

  const rows: [string, string | null][] = [
    ['Page', detail.page_url],
    ['Referrer', detail.referrer],
    ['Country', detail.country],
    ['Language', detail.language],
    ['Visitor', detail.visitor_id],
  ];

  return (
    <Card className="max-h-[calc(100vh-13rem)] overflow-y-auto p-4">
      <h3 className="text-sm font-semibold text-slate-900">Visitor</h3>
      <dl className="mt-2 space-y-2 text-xs">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="text-slate-400">{label}</dt>
            <dd className="break-words text-slate-700">{value || '—'}</dd>
          </div>
        ))}
        {Object.keys(detail.utm).length > 0 && (
          <div>
            <dt className="text-slate-400">UTM</dt>
            <dd className="break-words text-slate-700">
              {Object.entries(detail.utm)
                .map(([key, value]) => `${key}=${value}`)
                .join(', ')}
            </dd>
          </div>
        )}
      </dl>

      {detail.classification && (
        <div className="mt-4">
          <h3 className="mb-1 text-sm font-semibold text-slate-900">Classification</h3>
          <StatusBadge status={detail.classification} />
        </div>
      )}

      {detail.contacts.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-slate-900">Contact</h3>
          {detail.contacts.map((contact) => (
            <div key={contact.id} className="mt-1 text-xs text-slate-700">
              <div className="font-medium">{contact.name ?? 'Unnamed'}</div>
              <div>{contact.email}</div>
              <div>{contact.phone}</div>
              <div>{contact.company}</div>
              {contact.postal_code && <div>Zip: {contact.postal_code}</div>}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <h3 className="text-sm font-semibold text-slate-900">Internal note</h3>
        <textarea
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => void onSaveNote(note)}
          placeholder="Never shown to the visitor."
          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-brand-blue"
        />
      </div>
    </Card>
  );
}

/** Types a browser draws in an <img>. HEIC is accepted but most cannot show it. */
const PREVIEW_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
]);

/**
 * One file the visitor sent. Images are fetched (with the session token, which
 * a plain <img src> could not send) and shown as a thumbnail that opens full
 * size; PDFs open in a new tab; anything else is a download.
 */
function AttachmentCard({
  slug,
  conversationId,
  file,
  onError,
}: {
  slug: string;
  conversationId: string;
  file: ChatAttachment;
  onError: (message: string) => void;
}): JSX.Element {
  const isImage = PREVIEW_IMAGE_TYPES.has(file.content_type);
  const isPdf = file.content_type === 'application/pdf';
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [enlarged, setEnlarged] = useState(false);

  useEffect(() => {
    if (!isImage) return undefined;
    let url: string | null = null;
    let cancelled = false;
    api
      .attachmentBlob(slug, conversationId, file.id)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setImageUrl(url);
      })
      .catch(() => {
        // The card still offers the download; a missing thumbnail is not an error.
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [isImage, slug, conversationId, file.id]);

  function report(caught: unknown, fallback: string): void {
    onError(caught instanceof Error ? caught.message : fallback);
  }

  function download(): void {
    api
      .downloadAttachment(slug, conversationId, file.id, file.filename)
      .catch((caught: unknown) => report(caught, 'Could not download the file'));
  }

  function viewPdf(): void {
    // Opened before the fetch, while the click still counts as a user action,
    // so the popup blocker lets it through.
    const tab = window.open('', '_blank');
    api
      .attachmentBlob(slug, conversationId, file.id)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (tab) tab.location.href = url;
        else window.open(url, '_blank');
        // Long enough for the tab to load it.
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((caught: unknown) => {
        tab?.close();
        report(caught, 'Could not open the file');
      });
  }

  return (
    <div className="mt-1 w-fit max-w-full rounded-md border border-slate-300 bg-white p-2 text-sm">
      {isImage && (
        <button
          type="button"
          onClick={() => setEnlarged(true)}
          disabled={!imageUrl}
          className="mb-2 block overflow-hidden rounded bg-slate-100"
          aria-label={`Show ${file.filename} full size`}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={file.filename}
              className="max-h-48 max-w-[16rem] object-contain"
            />
          ) : (
            <div className="flex h-24 w-40 items-center justify-center text-xs text-slate-400">
              Loading preview…
            </div>
          )}
        </button>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span aria-hidden>📎</span>
        <span className="font-medium text-brand-navy">{file.filename}</span>
        <span className="text-xs text-slate-500">{formatBytes(file.size_bytes)}</span>
        {isPdf && (
          <button
            type="button"
            onClick={viewPdf}
            className="text-xs font-medium text-brand-blue underline"
          >
            View
          </button>
        )}
        <button
          type="button"
          onClick={download}
          className="text-xs font-medium text-brand-blue underline"
        >
          Download
        </button>
      </div>

      {enlarged && imageUrl && (
        <div
          role="dialog"
          aria-label={file.filename}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setEnlarged(false)}
        >
          <img
            src={imageUrl}
            alt={file.filename}
            className="max-h-full max-w-full rounded shadow-2xl"
          />
          <button
            type="button"
            onClick={() => setEnlarged(false)}
            className="absolute right-4 top-4 rounded bg-white/90 px-3 py-1 text-sm font-medium text-slate-800"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
